/**
 * Parametric Cuts tools (BIM: Steel > Parametric Cuts)
 *
 * One parameterized base plus four thin subclasses matching the ribbon:
 * - Modifier:   plane cut on a single member at the click station, optional
 *               angle (steelCutAngle) about the member's transverse axis.
 * - Cope:       two picks (coped member, then reference member). Relieves the
 *               coping member's end so it clears the reference's flange; depth
 *               follows the reference section plus steelCopeDistance clearance.
 * - Miter:      two picks; both ends are trimmed on the shared bisector plane.
 * - CutThrough: click along a member to punch a contour hole
 *               (steelCutThroughDiameter) in its web.
 *
 * Cuts live on the host element (`steelCuts`) so rendering and takeoff pick
 * them up without extra elements in the document.
 */

import { v4 as uuidv4 } from 'uuid';
import { BaseTool } from '../BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { Element, SteelCutData } from '../../core/types';
import {
    SteelFabricationManager,
    isSteelHostElement,
    resolveMemberFrame,
} from '../../core/SteelFabricationManager';
import { computeSteelMemberMetrics } from '../../utils/steelCutGeometry';
import i18n from '../../i18n';

abstract class SteelCutToolBase extends BaseTool {
    protected abstract kind: SteelCutData['kind'];
    /** How many steel members must be picked to produce the cut. */
    protected picksNeeded = 1;
    protected pickedIds: string[] = [];
    /** Surface point of the most recent pick, used to derive the station. */
    protected lastPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, el => isSteelHostElement(el));
        if (!hit) {
            this.notify(i18n.t('tools.notify.steelPickMember', { kind: this.kind.toLowerCase() }), 'warning');
            return;
        }

        const check = SteelFabricationManager.validateCutTarget(hit.element, this.kind);
        if (!check.valid) {
            this.notify(check.message ?? 'Invalid cut target.', 'error');
            return;
        }
        if (this.pickedIds.includes(hit.element.id)) return;

        this.pickedIds.push(hit.element.id);
        this.lastPoint = hit.point.clone();
        this.context.appStore.select([...this.pickedIds]);
        if (this.pickedIds.length >= this.picksNeeded) {
            this.applyCuts(this.pickedIds);
            this.reset();
        }
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void { }
    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Backspace' && this.pickedIds.length > 0) {
            this.pickedIds.pop();
            this.context?.appStore.select([...this.pickedIds]);
            event.preventDefault();
            return;
        }
        super.onKeyDown(event);
    }

    deactivate(): void {
        this.reset();
        super.deactivate();
    }

    protected reset(): void {
        this.pickedIds = [];
        this.context?.appStore.select([]);
        this.clearPreview();
    }

    protected abstract applyCuts(ids: string[]): void;

    /**
     * Commit a cut payload onto a host through the store, then refresh the
     * fabrication metrics (cut length / weight) so takeoff and the analytical
     * summary follow the trimmed geometry like BIM's auto-updating model.
     */
    protected commitCut(host: Element, cut: Omit<SteelCutData, 'id'>): void {
        if (!this.context) return;
        const fullCut: SteelCutData = {
            ...cut,
            id: uuidv4(),
            parameters: { ...cut.parameters },
        };
        const mergedCuts = [...(host.steelCuts ?? []), fullCut];

        // Metrics on the post-cut state; null when geometry can't resolve.
        const virtualHost: Element = { ...host, steelCuts: mergedCuts };
        const metrics = computeSteelMemberMetrics(virtualHost);

        const parameters = { ...host.parameters };
        let fabrication = (host as any).fabrication;
        if (metrics) {
            parameters['Cut Length'] = metrics.cutLength;
            parameters['Weight'] = metrics.weightKg;
            parameters['Hole Volume'] = metrics.holeVolume;
            if (fabrication) {
                fabrication = { ...fabrication, weight: metrics.weightKg };
            }
        }

        this.context.bimStore.updateElement(host.id, {
            steelCuts: mergedCuts,
            parameters,
            ...(fabrication ? { fabrication } : {}),
        } as Partial<Element>);
    }
}

/**
 * Modifier: single pick. The click station along the member axis becomes the
 * cut plane; steelCutAngle tilts it (degrees) about the member's side axis.
 */
export class SteelModifierTool extends SteelCutToolBase {
    id = 'steel_modifier';
    name = 'Modifier';
    cursor = 'crosshair';
    protected kind: SteelCutData['kind'] = 'Modifier';

    protected applyCuts(ids: string[]): void {
        if (!this.context) return;
        const host = this.context.bimStore.project.elements.get(ids[0]);
        const frame = host ? resolveMemberFrame(host) : null;
        if (!host || !frame) return;

        const length = frame.start.distanceTo(frame.end);
        const dir = frame.end.clone().sub(frame.start).normalize();
        const angleDeg = this.context.bimStore.toolProperties.steelCutAngle ?? 0;
        const ref = Math.abs(dir.z) < 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(1, 0, 0);
        const side = new THREE.Vector3().crossVectors(dir, ref).normalize();
        const normal = dir.clone().applyAxisAngle(side, THREE.MathUtils.degToRad(angleDeg)).normalize();

        // Cut the member end nearest the click at the clicked station.
        const click = this.lastPoint ?? frame.end;
        const t = THREE.MathUtils.clamp(click.clone().sub(frame.start).dot(dir) / Math.max(length, 1e-6), 0.05, 0.95);
        const at = t <= 0.5 ? 'Start' : 'End';
        const station = frame.start.clone().addScaledVector(dir, t * length);
        this.commitCut(host, {
            kind: 'Modifier',
            at,
            plane: { normal: [normal.x, normal.y, normal.z], origin: [station.x, station.y, station.z] },
            angle: angleDeg,
            parameters: { 'Angle': angleDeg, 'Station': t },
        });
        this.notify(i18n.t('tools.notify.modifierCutApplied', { suffix: angleDeg ? i18n.t('tools.notify.cutAtAngle', { angle: angleDeg }) : '' }), 'success');
    }
}

/**
 * Cope: pick the member to cope, then the member to fit against. The cope
 * geometry mirrors the reference's flange depth with a parametric clearance.
 */
export class SteelCopeTool extends SteelCutToolBase {
    id = 'steel_cope';
    name = 'Cope';
    cursor = 'crosshair';
    protected kind: SteelCutData['kind'] = 'Cope';
    protected picksNeeded = 2;

    protected applyCuts(ids: string[]): void {
        if (!this.context) return;
        const target = this.context.bimStore.project.elements.get(ids[0]);
        const reference = this.context.bimStore.project.elements.get(ids[1]);
        const frameT = target ? resolveMemberFrame(target) : null;
        const frameR = reference ? resolveMemberFrame(reference) : null;
        if (!target || !frameT || !frameR) return;

        // Cope the end of the target closest to the reference member.
        const rMid = frameR.start.clone().add(frameR.end).multiplyScalar(0.5);
        const at = frameT.start.distanceTo(rMid) <= frameT.end.distanceTo(rMid) ? 'Start' : 'End';
        const clearance = Math.max((this.context.bimStore.toolProperties.steelCopeDistance ?? 2) / 1000, 0.001);

        this.commitCut(target, {
            kind: 'Cope',
            at,
            // Cope depth: reference member half-depth + clearance; cope length:
            // target width + clearance (the material that must disappear).
            depth: frameR.depth / 2 + clearance,
            length: frameT.width + clearance,
            flange: 'Top',
            referenceElementId: reference?.id,
            parameters: { 'Coping Distance': clearance * 1000 },
        });
        this.notify(i18n.t('tools.notify.copeCutApplied', { clearance: (clearance * 1000).toFixed(0) }), 'success');
    }
}

/**
 * Miter: pick two members; both are trimmed on the bisector plane at their
 * shared junction so the frame corner reads as one continuous line.
 */
export class SteelMiterTool extends SteelCutToolBase {
    id = 'steel_miter';
    name = 'Miter';
    cursor = 'crosshair';
    protected kind: SteelCutData['kind'] = 'Miter';
    protected picksNeeded = 2;

    protected applyCuts(ids: string[]): void {
        if (!this.context) return;
        const a = this.context.bimStore.project.elements.get(ids[0]);
        const b = this.context.bimStore.project.elements.get(ids[1]);
        const fa = a ? resolveMemberFrame(a) : null;
        const fb = b ? resolveMemberFrame(b) : null;
        if (!a || !b || !fa || !fb) return;

        const da = fa.end.clone().sub(fa.start).normalize();
        const db = fb.end.clone().sub(fb.start).normalize();
        // Junction: the endpoints pair closest to each other.
        const candidates: Array<{ pa: THREE.Vector3; pb: THREE.Vector3; atA: 'Start' | 'End'; atB: 'Start' | 'End' }> = [
            { pa: fa.start, pb: fb.start, atA: 'Start', atB: 'Start' },
            { pa: fa.start, pb: fb.end, atA: 'Start', atB: 'End' },
            { pa: fa.end, pb: fb.start, atA: 'End', atB: 'Start' },
            { pa: fa.end, pb: fb.end, atA: 'End', atB: 'End' },
        ];
        let best = candidates[0];
        for (const c of candidates) {
            if (c.pa.distanceTo(c.pb) < best.pa.distanceTo(best.pb)) best = c;
        }
        const junction = best.pa.clone().add(best.pb).multiplyScalar(0.5);

        // Bisector plane normal: sum of both member directions pointing away
        // from the junction (miter line between them).
        const awayA = (best.atA === 'Start' ? da.clone().negate() : da.clone());
        const awayB = (best.atB === 'Start' ? db.clone().negate() : db.clone());
        const normal = awayA.add(awayB).normalize();
        if (normal.lengthSq() < 1e-8) normal.set(0, 0, 1);

        const plane = {
            normal: [normal.x, normal.y, normal.z] as [number, number, number],
            origin: [junction.x, junction.y, junction.z] as [number, number, number],
        };
        this.commitCut(a, { kind: 'Miter', at: best.atA, plane, parameters: {} });
        this.commitCut(b, { kind: 'Miter', at: best.atB, plane, parameters: {} });
        this.notify(i18n.t('tools.notify.miterCutAppliedToBothMembers'), 'success');
    }
}

/**
 * Cut Through: single pick. Places a circular contour hole at the clicked
 * station along the member's web. Diameter from steelCutThroughDiameter (mm).
 */
export class SteelCutThroughTool extends SteelCutToolBase {
    id = 'steel_cut_through';
    name = 'Cut Through';
    cursor = 'crosshair';
    protected kind: SteelCutData['kind'] = 'CutThrough';

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, el => isSteelHostElement(el));
        if (!hit) {
            this.notify(i18n.t('tools.notify.pickAStructuralSteelMember'), 'warning');
            return;
        }
        const frame = resolveMemberFrame(hit.element);
        if (!frame) return;

        const dir = frame.end.clone().sub(frame.start).normalize();
        const t = THREE.MathUtils.clamp(hit.point.clone().sub(frame.start).dot(dir) / frame.start.distanceTo(frame.end), 0.05, 0.95);

        // Local contour polygon: circle in [along-axis, transverse] meters.
        const dM = Math.max((this.context.bimStore.toolProperties.steelCutThroughDiameter ?? 50) / 1000, 0.01);
        const r = dM / 2;
        const contour: [number, number][] = [];
        const segments = 16;
        for (let i = 0; i < segments; i++) {
            const a = (i / segments) * Math.PI * 2;
            contour.push([Math.cos(a) * r, Math.sin(a) * r]);
        }

        this.commitCut(hit.element, {
            kind: 'CutThrough',
            at: 'Body',
            contour,
            depth: r,
            parameters: { 'Hole Diameter': dM * 1000, 'Station': t },
        });
        this.notify(i18n.t('tools.notify.contourCutPlaced', { percent: (t * 100).toFixed(0) }), 'success');
        this.reset();
    }

    protected applyCuts(_ids: string[]): void {
        // Unused: CutThrough commits directly from the click handler so the
        // click point (not just the element) drives the hole station.
    }
}
